import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTheme } from "next-themes";
import { useAuth } from "@/context/AuthContext";
import FeedbackDialog from "@/components/FeedbackDialog";
import BrandLockup from "@/components/BrandLockup";
import {
  CaretDown,
  ChartBar,
  ChatCircleDots,
  Desktop,
  House,
  List,
  Moon,
  PencilSimple,
  PlugsConnected,
  SignOut,
  Sun,
  UploadSimple,
  Target,
  Users,
  X,
} from "@phosphor-icons/react";

const navItems = [
  { to: "/dashboard", icon: House, label: "Dashboard", testId: "nav-dashboard", exact: true },
  { to: "/upload", icon: UploadSimple, label: "New call", testId: "nav-upload", primary: true },
  { to: "/integrations", icon: PlugsConnected, label: "Integrations", testId: "nav-integrations" },
  { to: "/customers", icon: Users, label: "Customers", testId: "nav-customers" },
  { to: "/prepare-next-call", icon: Target, label: "Prepare Next Call", testId: "nav-prepare-next-call" },
  { to: "/profile", icon: PencilSimple, label: "Writing DNA", testId: "nav-profile" },
];

const ownerNavItem = {
  to: "/admin",
  icon: ChartBar,
  label: "Beta overview",
  testId: "nav-admin",
};

const themeOptions = [
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
  { value: "system", label: "System", icon: Desktop },
];

function initials(user) {
  const source = user?.name || user?.email || "Callsect";
  const words = source.trim().split(/\s+/).filter(Boolean);
  if (words.length > 1) return `${words[0][0]}${words[1][0]}`.toUpperCase();
  return source.slice(0, 2).toUpperCase();
}

export default function Nav() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const { theme, setTheme } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [themeOpen, setThemeOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const themeMenuRef = useRef(null);

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    setMobileOpen(false);
    setThemeOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [mobileOpen]);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const handleEscape = (event) => {
      if (event.key === "Escape") setMobileOpen(false);
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [mobileOpen]);

  useEffect(() => {
    const closeThemeMenu = (event) => {
      if (themeMenuRef.current && !themeMenuRef.current.contains(event.target)) {
        setThemeOpen(false);
      }
    };
    document.addEventListener("pointerdown", closeThemeMenu);
    return () => document.removeEventListener("pointerdown", closeThemeMenu);
  }, []);

  const isActive = (to, exact = false) =>
    exact ? location.pathname === to : location.pathname === to || location.pathname.startsWith(`${to}/`);

  const selectedTheme = mounted ? theme || "system" : "system";
  const activeTheme = themeOptions.find((option) => option.value === selectedTheme) || themeOptions[2];
  const ActiveThemeIcon = activeTheme.icon;

  const focusAppThemeItem = (menu, index) => {
    const items = Array.from(menu?.querySelectorAll('[role="menuitemradio"]') || []);
    if (!items.length) return;
    const normalized = (index + items.length) % items.length;
    items[normalized]?.focus();
  };

  const handleAppThemeTriggerKeyDown = (event) => {
    if (event.key === "Escape" && themeOpen) {
      event.preventDefault();
      setThemeOpen(false);
      return;
    }
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    event.preventDefault();
    const root = event.currentTarget.parentElement;
    setThemeOpen(true);
    window.requestAnimationFrame(() => {
      const menu = root?.querySelector('.app-theme-menu');
      const selectedIndex = Math.max(0, themeOptions.findIndex((option) => option.value === selectedTheme));
      focusAppThemeItem(menu, selectedIndex);
    });
  };

  const handleAppThemeMenuKeyDown = (event) => {
    const menu = event.currentTarget;
    const items = Array.from(menu.querySelectorAll('[role="menuitemradio"]'));
    if (!items.length) return;
    const currentIndex = Math.max(0, items.indexOf(document.activeElement));

    if (event.key === "Escape") {
      event.preventDefault();
      setThemeOpen(false);
      menu.parentElement?.querySelector('.app-theme-control__trigger')?.focus();
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      focusAppThemeItem(menu, currentIndex + 1);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      focusAppThemeItem(menu, currentIndex - 1);
    } else if (event.key === "Home") {
      event.preventDefault();
      focusAppThemeItem(menu, 0);
    } else if (event.key === "End") {
      event.preventDefault();
      focusAppThemeItem(menu, items.length - 1);
    }
  };

  const navigationLinks = (mobile = false) => (
    <div className="app-sidebar__nav" aria-label="Primary navigation">
      <span className="app-sidebar__section-label">Workspace</span>
      {[...navItems, ...(user?.account_type === "owner" ? [ownerNavItem] : [])].map(
        ({ to, icon: Icon, label, testId, exact, primary }) => {
        const active = isActive(to, exact);
        return (
          <Link
            key={to}
            to={to}
            data-testid={testId}
            aria-current={active ? "page" : undefined}
            className={`app-sidebar__link${active ? " is-active" : ""}${primary ? " is-primary-action" : ""}`}
            onClick={() => mobile && setMobileOpen(false)}
          >
            <span className="app-sidebar__link-icon" aria-hidden="true">
              <Icon size={18} weight={active ? "duotone" : "regular"} />
            </span>
            <span>{label}</span>
            {primary && <span className="app-sidebar__link-badge">New</span>}
          </Link>
        );
      })}
    </div>
  );

  const sidebarFooter = (mobile = false) => (
    <div className="app-sidebar__footer">
      <button
        type="button"
        className="app-feedback-button"
        onClick={() => {
          setFeedbackOpen(true);
          if (mobile) setMobileOpen(false);
        }}
      >
        <span aria-hidden="true"><ChatCircleDots size={17} weight="duotone" /></span>
        <span>Share feedback</span>
      </button>

      <div className="app-theme-control" ref={themeMenuRef}>
        <button
          type="button"
          className="app-theme-control__trigger"
          onClick={() => setThemeOpen((open) => !open)}
          onKeyDown={handleAppThemeTriggerKeyDown}
          aria-haspopup="menu"
          aria-expanded={themeOpen}
        >
          <span className="app-theme-control__icon" aria-hidden="true">
            <ActiveThemeIcon size={17} />
          </span>
          <span>{activeTheme.label}</span>
          <CaretDown size={14} className={themeOpen ? "is-open" : ""} />
        </button>

        {themeOpen && (
          <div
            className="app-theme-menu"
            role="menu"
            aria-label="Appearance"
            onKeyDown={handleAppThemeMenuKeyDown}
            style={mobile ? { top: "auto", bottom: "calc(100% + 0.45rem)" } : undefined}
          >
            {themeOptions.map(({ value, label, icon: Icon }) => (
              <button
                key={value}
                type="button"
                role="menuitemradio"
                aria-checked={selectedTheme === value}
                tabIndex={selectedTheme === value ? 0 : -1}
                className={selectedTheme === value ? "is-selected" : ""}
                onClick={(event) => {
                  const control = event.currentTarget.closest('.app-theme-control');
                  setTheme(value);
                  if (typeof window !== "undefined") {
                    try {
                      window.localStorage.removeItem("callsect-theme");
                      if (value === "system") window.localStorage.removeItem("callsect-public-theme");
                    } catch {
                      // Keep the live theme selection even when storage is unavailable.
                    }
                  }
                  setThemeOpen(false);
                  window.requestAnimationFrame(() => control?.querySelector('.app-theme-control__trigger')?.focus());
                }}
              >
                <Icon size={16} />
                <span>{label}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="app-account-card">
        <Link
          to="/account"
          data-testid="nav-account"
          aria-current={isActive("/account") ? "page" : undefined}
          className={`app-account-card__profile${isActive("/account") ? " is-active" : ""}`}
          onClick={() => mobile && setMobileOpen(false)}
        >
          <span className="app-account-card__avatar" aria-hidden="true">{initials(user)}</span>
          <span className="app-account-card__copy">
            <strong>{user?.name || "Account"}</strong>
            <small>{user?.email || "Manage your account"}</small>
          </span>
        </Link>
        <button
          type="button"
          onClick={logout}
          data-testid="logout-btn"
          aria-label="Sign out"
          title="Sign out"
          className="app-account-card__logout"
        >
          <SignOut size={17} />
        </button>
      </div>
    </div>
  );

  return (
    <div className="app-navigation">
      <aside className="app-sidebar" aria-label="Callsect navigation">
        <Link
          to="/dashboard"
          className="app-brand"
          data-testid="brand-link"
          aria-label="Callsect dashboard"
        >
          <BrandLockup className="brand-lockup--sidebar" />
        </Link>

        {navigationLinks()}
        {sidebarFooter()}
      </aside>

      <header className="app-mobile-bar">
        <Link to="/dashboard" className="app-mobile-brand" aria-label="Callsect dashboard">
          <BrandLockup className="brand-lockup--mobile-nav" />
        </Link>
        <div className="app-mobile-actions">
          <Link to="/upload" className="app-mobile-new-call" aria-label="Upload a new call">
            <UploadSimple size={18} weight="bold" />
            <span>New call</span>
          </Link>
          <button
          type="button"
          className="app-mobile-menu-button"
          onClick={() => setMobileOpen(true)}
          aria-label="Open navigation"
          aria-expanded={mobileOpen}
        >
            <List size={22} />
          </button>
        </div>
      </header>

      {mobileOpen && (
        <div className="app-mobile-drawer" role="dialog" aria-modal="true" aria-label="Navigation menu">
          <button
            type="button"
            className="app-mobile-drawer__backdrop"
            onClick={() => setMobileOpen(false)}
            aria-label="Close navigation"
          />
          <aside
            className="app-mobile-drawer__panel"
            style={{ overflowY: "auto", overscrollBehavior: "contain" }}
          >
            <div className="app-mobile-drawer__header">
              <Link to="/dashboard" className="app-mobile-brand" onClick={() => setMobileOpen(false)}>
                <BrandLockup className="brand-lockup--mobile-nav" />
              </Link>
              <button type="button" onClick={() => setMobileOpen(false)} aria-label="Close navigation">
                <X size={20} />
              </button>
            </div>
            {navigationLinks(true)}
            {sidebarFooter(true)}
          </aside>
        </div>
      )}
      <FeedbackDialog
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
        pagePath={location.pathname}
      />
    </div>
  );
}
